import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    List,
    ListItem,
    ListItemText,
    Divider
} from "@mui/material";
import { useGenericMutation } from "../../services/react-query/generic.mutation";
import { UserTypes } from "../../types";
import Papa from "papaparse";
import React, { useRef, useState } from "react";
import { Dropzone } from "../dropzone";
import { toast } from "react-toastify";
import { LoadingButton } from "@mui/lab";
import { handleApiError } from "../../services/react-query/react-query.client";
import Loading from "../loading";
import { When } from "react-if";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DownloadIcon from '@mui/icons-material/Download';
import { CSV_TEMPLATE_SAMPLE_VALUES } from '../../constants/csvTemplateSampleData.constants';

// Maximum number of users that can be imported at once
const IMPORT_USERS_LIMIT = 5000;

interface Props {
    visible: boolean;
    onClose: () => void;
    onSuccess: (results: UserTypes.ImportExecuteResponse) => void;
    setShowActiveUsers: (flag: string) => void;
    toggleRefresh: boolean;
    setToggleRefresh: (flag: boolean) => void;
    uniqueIdentifier?: string;
    isCustomerSettingsLoading?: boolean;
}

export const ImportUsersModal: React.FC<Props> = ({
    visible,
    onClose,
    onSuccess,
    setShowActiveUsers,
    toggleRefresh,
    setToggleRefresh,
    uniqueIdentifier,
    isCustomerSettingsLoading,
}) => {
    // States
    const [error, setError] = useState<string | null>(null);
    const [file64, setFile64] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File>();
    const [previewData, setPreviewData] = useState<UserTypes.ImportPreviewResponse | null>(null);
    const [previewExpanded, setPreviewExpanded] = useState<boolean>(false);
    const previewRef = useRef<HTMLDivElement>(null);

    // Mutations
    const {
        mutateAsync: previewMutation,
        isLoading: isPreviewLoading,
    } = useGenericMutation<{ rows: UserTypes.ImportUserRow[] }, UserTypes.ImportPreviewResponse>(
        'previewImportUsers',
        'users/import/preview',
        'post'
    );

    const {
        mutateAsync: executeMutation,
        isLoading: isExecuteLoading,
    } = useGenericMutation<{ rows: UserTypes.ImportPreviewRow[] }, UserTypes.ImportExecuteResponse>(
        'executeImportUsers',
        'users/import/execute',
        'post'
    );

    // Functions
    const checkForUniqueFieldFlag = (userData: UserTypes.ImportUserLowerCase): boolean => {
        if (!uniqueIdentifier || uniqueIdentifier === "INVITECODE")
            return !!userData.phonenumber;
        else if (uniqueIdentifier === "EMPLOYEEID")
            return !!userData.employeeid;
        else if (uniqueIdentifier === "EMAILADDRESS")
            return !!userData.backupemail;
        return true;
    };

    const getNoteText = (): string => {
        if (!uniqueIdentifier || uniqueIdentifier === "INVITECODE")
            return "NOTE: firstName, lastName and phoneNumber are required fields. Other fields are optional";
        else if (uniqueIdentifier === "EMAILADDRESS")
            return "NOTE: firstName, lastName and backupEmail are required fields. Other fields are optional";
        else if (uniqueIdentifier === "EMPLOYEEID")
            return "NOTE: firstName, lastName and employeeId are required fields. Other fields are optional";
        return "NOTE: firstName and lastName are required fields. Other fields are optional";
    };

    const fetchPreview = async (rows: UserTypes.ImportUserRow[]) => {
        try {
            const result = await previewMutation({ rows });
            setPreviewData(result.result);
            setPreviewExpanded(true);

            // Auto-scroll to preview after a short delay
            setTimeout(() => {
                previewRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest'
                });
            }, 100);
        } catch (err) {
            const msg = handleApiError(err);
            toast.error(`Preview failed: ${msg}`);
            setPreviewData(null);
        }
    };

    const parseCSV = async (file: File) => {
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            transformHeader: (header) => {
                const normalized = header.replace(/\(required\)/i, '').trim().toLowerCase().replace(/[^a-z]/g, '');
                // Remap short column names (from unified templates) to the long names used by the import parser
                const aliases: Record<string, string> = {
                    phone: 'phonenumber',
                    email: 'backupemail',
                    group: 'groupname',
                    team: 'teamname',
                };
                return aliases[normalized] || normalized;
            },
            complete: async (result: any) => {
                const data = result.data as UserTypes.ImportUserLowerCase[];

                if (data.length > 0) {
                    const sanitizedData: UserTypes.ImportUserRow[] = [];

                    data?.forEach((d) => {
                        if (d.firstname && d.lastname && checkForUniqueFieldFlag(d)) {
                            // Extract tags from tag columns (tagone, tagtwo, tagthree, etc.)
                            const userTags: string[] = [];
                            Object.keys(d).forEach(key => {
                                if (key.startsWith('tag') && d[key] && d[key]!.trim() !== '') {
                                    userTags.push(d[key]!.trim());
                                }
                            });

                            sanitizedData.push({
                                firstName: d.firstname?.trim(),
                                lastName: d.lastname?.trim(),
                                phoneNumber: d.phonenumber && d.phonenumber.trim() !== '' ? d.phonenumber.trim() : undefined,
                                backupEmail: d.backupemail && d.backupemail.trim() !== '' ? d.backupemail.trim() : undefined,
                                employeeId: d.employeeid && d.employeeid.trim() !== '' ? d.employeeid.trim() : undefined,
                                jobTitle: d.jobtitle && d.jobtitle.trim() !== '' ? d.jobtitle.trim() : undefined,
                                groupName: d.groupname && d.groupname.trim() !== '' ? d.groupname.trim() : undefined,
                                teamName: d.teamname && d.teamname.trim() !== '' ? d.teamname.trim() : undefined,
                                tags: userTags.length > 0 ? userTags : undefined,
                            });
                        }
                    });

                    if (sanitizedData.length === 0) {
                        toast.error('No valid user data found in file. Check formatting.');
                        setPreviewData(null);
                    } else if (sanitizedData.length > IMPORT_USERS_LIMIT) {
                        // Check row limit after sanitization
                        toast.error(`File contains ${sanitizedData.length} valid users. Maximum is ${IMPORT_USERS_LIMIT} users per import.`);
                        setPreviewData(null);
                    } else {
                        // Call preview endpoint
                        await fetchPreview(sanitizedData);
                    }
                }
            },
            error: () => {
                toast.error('An error occurred while trying to import from the file, format is invalid');
            },
        });
    };

    const onSubmit = async () => {
        if (!previewData) return;

        try {
            const result = await executeMutation({ rows: previewData.validRows });

            // Reset state
            setFile64(null);
            setSelectedFile(undefined);
            setPreviewData(null);
            setPreviewExpanded(false);
            onClose();
            setToggleRefresh(!toggleRefresh);
            setShowActiveUsers('false');

            // Call onSuccess callback to open results modal
            onSuccess(result.result);
        } catch (err) {
            // For complete failures (500 errors), keep modal open and show error toast
            const msg = handleApiError(err);
            setError(msg);
            toast.error(`Import failed: ${msg}`);
        }
    };

    const downloadPreviewReport = () => {
        if (!previewData) return;

        // Prevent CSV formula injection — prefix dangerous leading characters
        const sanitize = (val: string): string => {
            if (/^[=+\-@\t\r]/.test(val)) return `'${val}`;
            return val;
        };

        const mapRow = (row: UserTypes.ImportPreviewRow) => ({
            'Status': row.status === 'valid' ? 'Valid' : row.status === 'duplicate' ? 'Duplicate' : 'Error',
            'Row #': row.rowIndex + 2,
            'First Name': sanitize(row.firstName || ''),
            'Last Name': sanitize(row.lastName || ''),
            'Phone': sanitize(row.phoneNumber || ''),
            'Email': sanitize(row.backupEmail || ''),
            'Employee ID': sanitize(row.employeeId || ''),
            'Job Title': sanitize(row.jobTitle || ''),
            'Group': sanitize(row.groupName || ''),
            'Team': sanitize(row.teamName || ''),
            'Tags': sanitize(row.tags?.join('; ') || ''),
            'Error Type': row.errorType || '',
            'Error Message': sanitize(row.errorMessage || ''),
        });

        const allRows = [
            ...previewData.validRows.map(mapRow),
            ...previewData.duplicateRows.map(mapRow),
            ...previewData.errorRows.map(mapRow),
        ];

        const csv = Papa.unparse(allRows);
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const element = document.createElement('a');
        element.href = url;
        element.download = `import-preview-report-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
        URL.revokeObjectURL(url);
    };

    const hasErrors = previewData && previewData.errorRows.length > 0;
    const hasDuplicates = previewData && previewData.duplicateRows.length > 0;
    const hasWarnings = previewData && previewData.summary.teamsNotFound.length > 0;

    // Rendering
    return (
        <Dialog
            scroll={'paper'}
            onClose={onClose}
            open={visible}
            maxWidth="md"
            fullWidth
        >
            <When condition={isCustomerSettingsLoading}>
                <Loading />
            </When>
            <When condition={!isCustomerSettingsLoading}>
                <DialogTitle fontWeight={500} mb={1} id="alert-dialog-title">
                    Import POM Users
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Upload a .csv file of POM users to import them to the system.
                    </DialogContentText>
                    <Grid display={'flex'} alignItems={'center'} justifyItems={'center'} alignSelf={'center'} flexDirection={'row'} spacing={3} mb={3}>
                        <Button
                            size="small"
                            variant="text"
                            sx={{ mt: 2 }}
                            onClick={() => {
                                const identifierColumn = !uniqueIdentifier || uniqueIdentifier === 'INVITECODE'
                                    ? 'Phone (required)'
                                    : uniqueIdentifier === 'EMAILADDRESS'
                                        ? 'Email (required)'
                                        : 'Employee ID (required)';

                                const columns = [
                                    'First Name (required)', 'Last Name (required)', identifierColumn,
                                    'Phone', 'Email', 'Employee ID',
                                    'Job Title', 'Group', 'Team',
                                    'Tag One', 'Tag Two', 'Tag Three',
                                ].filter((c) => {
                                    // Remove the non-required duplicate of the identifier column
                                    const requiredLabel = identifierColumn.replace(' (required)', '');
                                    return c !== requiredLabel || c === identifierColumn;
                                });

                                const sampleRow: Record<string, string> = {};
                                for (const col of columns) {
                                    sampleRow[col] = CSV_TEMPLATE_SAMPLE_VALUES[col] || '';
                                }

                                const csv = Papa.unparse({ fields: columns, data: [sampleRow] });

                                const element = document.createElement('a');
                                const file = new Blob([csv], { type: 'text/csv' });
                                const url = URL.createObjectURL(file);
                                element.href = url;
                                element.download = 'import-users-template.csv';
                                document.body.appendChild(element);
                                element.click();

                                // Cleanup after download
                                document.body.removeChild(element);
                                URL.revokeObjectURL(url);
                            }}
                        >
                            Download csv template
                        </Button>
                    </Grid>

                    {error &&
                        <Alert variant="standard" color="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    }

                    <Dropzone
                        file={file64 || undefined}
                        selectedFile={selectedFile}
                        onChange={(b64, file) => {
                            if (file.type !== 'text/csv') {
                                toast.error('Invalid file type, should be a .csv file.');
                                return;
                            }
                            setSelectedFile(file);
                            setFile64(b64);
                            parseCSV(file);
                        }}
                    />

                    {/* Preview Loading State */}
                    {isPreviewLoading && (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Loading />
                            <Typography variant="body2" color="text.secondary">
                                Analyzing CSV file...
                            </Typography>
                        </Box>
                    )}

                    {/* Preview Section */}
                    {previewData && (
                        <Accordion
                            ref={previewRef}
                            expanded={previewExpanded}
                            onChange={() => setPreviewExpanded(!previewExpanded)}
                            sx={{ mt: 2 }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                    {hasErrors ? (
                                        <ErrorOutlineIcon color="error" />
                                    ) : hasWarnings ? (
                                        <WarningAmberIcon color="warning" />
                                    ) : (
                                        <CheckCircleOutlineIcon color="success" />
                                    )}
                                    <Typography fontWeight={500}>
                                        Import Preview
                                    </Typography>
                                    {hasErrors && (
                                        <Chip
                                            label={`${previewData.errorRows.length} Errors`}
                                            color="error"
                                            size="small"
                                            sx={{ ml: 1 }}
                                        />
                                    )}
                                    {hasDuplicates && (
                                        <Chip
                                            label={`${previewData.duplicateRows.length} Duplicates`}
                                            color="default"
                                            size="small"
                                            sx={{ ml: 1 }}
                                        />
                                    )}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {/* Summary */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                        Summary
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={4}>
                                            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                                <Typography variant="h4" color="primary">
                                                    {previewData.summary.valid}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Users to Import
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                                <Typography variant="h4" color="success.main">
                                                    {previewData.summary.willBeAssignedToTeam}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    With Team
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                                <Typography variant="h4" color="text.secondary">
                                                    {previewData.summary.willNotBeAssignedToTeam}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    No Team
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                    <Button
                                        size="small"
                                        variant="text"
                                        startIcon={<DownloadIcon />}
                                        onClick={downloadPreviewReport}
                                        sx={{ mt: 1 }}
                                    >
                                        Download Preview Report
                                    </Button>
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                {/* Errors - Cannot Import */}
                                {hasErrors && (
                                    <Box sx={{ mb: 3 }}>
                                        <Alert severity="error" icon={<ErrorOutlineIcon />}>
                                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                                Errors - Cannot Import ({previewData.errorRows.length})
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                These rows have conflicts and must be fixed before importing:
                                            </Typography>
                                            <List dense>
                                                {previewData.errorRows.slice(0, 5).map((row, idx) => (
                                                    <ListItem key={idx} sx={{ py: 0.5 }}>
                                                        <ListItemText
                                                            primary={`Row ${row.rowIndex + 2}: ${row.firstName} ${row.lastName}`}
                                                            secondary={row.errorMessage}
                                                            primaryTypographyProps={{ variant: 'body2' }}
                                                            secondaryTypographyProps={{ variant: 'caption', color: 'error' }}
                                                        />
                                                    </ListItem>
                                                ))}
                                                {previewData.errorRows.length > 5 && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                                                        ...and {previewData.errorRows.length - 5} more
                                                    </Typography>
                                                )}
                                            </List>
                                        </Alert>
                                    </Box>
                                )}

                                {/* Duplicates - Already Exist */}
                                {hasDuplicates && (
                                    <Box sx={{ mb: 3 }}>
                                        <Alert severity="info" icon={<InfoOutlinedIcon />}>
                                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                                Duplicates - Already Exist ({previewData.duplicateRows.length})
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                These users already exist and will be skipped:
                                            </Typography>
                                            <List dense>
                                                {previewData.duplicateRows.slice(0, 5).map((row, idx) => (
                                                    <ListItem key={idx} sx={{ py: 0.5 }}>
                                                        <ListItemText
                                                            primary={`${row.firstName} ${row.lastName}`}
                                                            secondary={row.phoneNumber || row.backupEmail || row.employeeId}
                                                            primaryTypographyProps={{ variant: 'body2' }}
                                                            secondaryTypographyProps={{ variant: 'caption' }}
                                                        />
                                                    </ListItem>
                                                ))}
                                                {previewData.duplicateRows.length > 5 && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                                                        ...and {previewData.duplicateRows.length - 5} more
                                                    </Typography>
                                                )}
                                            </List>
                                        </Alert>
                                    </Box>
                                )}

                                {/* Warnings - Teams Not Found */}
                                {hasWarnings && (
                                    <Box sx={{ mb: 3 }}>
                                        <Alert severity="warning" icon={<WarningAmberIcon />}>
                                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                                Teams Not Found ({previewData.summary.teamsNotFound.length})
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                Users with these team assignments will be imported without a team:
                                            </Typography>
                                            <List dense>
                                                {previewData.summary.teamsNotFound.slice(0, 5).map((teamPath, idx) => (
                                                    <ListItem key={idx} sx={{ py: 0.5 }}>
                                                        <ListItemText
                                                            primary={teamPath}
                                                            primaryTypographyProps={{ variant: 'body2' }}
                                                        />
                                                    </ListItem>
                                                ))}
                                                {previewData.summary.teamsNotFound.length > 5 && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                                                        ...and {previewData.summary.teamsNotFound.length - 5} more
                                                    </Typography>
                                                )}
                                            </List>
                                        </Alert>
                                    </Box>
                                )}

                                {/* New Tags to Create */}
                                {previewData.summary.newTagsToCreate.length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                            New Tags to be Created ({previewData.summary.newTagsToCreate.length})
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {previewData.summary.newTagsToCreate.map((tag, idx) => (
                                                <Chip key={idx} label={tag} color="success" variant="outlined" size="small" />
                                            ))}
                                        </Box>
                                    </Box>
                                )}

                                {/* Info */}
                                <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mt: 2 }}>
                                    <Typography variant="body2">
                                        Using <strong>{previewData.info.uniqueIdentifierField}</strong> as the unique identifier for matching users.
                                    </Typography>
                                </Alert>
                            </AccordionDetails>
                        </Accordion>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button disabled={isExecuteLoading} color="inherit" onClick={onClose}>
                        Cancel
                    </Button>
                    <LoadingButton
                        onClick={onSubmit}
                        loading={isExecuteLoading}
                        disabled={!previewData || previewData.validRows.length === 0}
                        autoFocus
                    >
                        Confirm Import
                    </LoadingButton>
                </DialogActions>
            </When>
        </Dialog>
    );
};
